import { useCallback, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { buildPuzzleFeedPageView, isPuzzleFeedPath, PUZZLE_VIEW_PARAM } from "@/helpers/puzzle-view-url";

type UsePuzzleFeedPageViewResult = {
    syncFeedPageView: (loadedPageCount: number) => void;
};

/**
 * Keeps `?view=pageN` in sync with feed infinite-scroll batches on `/` or `/puzzle` (Raptive-friendly search updates).
 */
export function usePuzzleFeedPageView(): UsePuzzleFeedPageViewResult {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const lastSyncedPageRef = useRef<number>(0);

    const syncFeedPageView = useCallback(
        (loadedPageCount: number) => {
            if (!isPuzzleFeedPath(location.pathname)) {
                return;
            }
            const pageNumber = Math.max(1, Math.floor(loadedPageCount));
            const targetView = buildPuzzleFeedPageView(pageNumber);
            if (searchParams.get(PUZZLE_VIEW_PARAM) === targetView && lastSyncedPageRef.current === pageNumber) {
                return;
            }
            lastSyncedPageRef.current = pageNumber;
            const params = new URLSearchParams(searchParams);
            params.set(PUZZLE_VIEW_PARAM, targetView);
            setSearchParams(params, { replace: true });
        },
        [location.pathname, searchParams, setSearchParams],
    );

    return { syncFeedPageView };
}
